import React from 'react'
import TableSortLabel from '@mui/material/TableSortLabel'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import { Box } from '@mui/material'
import { SortField } from '../../../interfaces/DataGrid/DataGridSettings'

type Order = 'asc' | 'desc'

export interface HeadCell<T> {
  id: keyof T
  numeric: boolean
  disablePadding: boolean
  label: string
  sortable: boolean
  visible: boolean
}

interface TableHeaderProps<T> {
  numSelected: number
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void
  orderInitial: Order
  orderByInitial: keyof T
  rowCount: number
  sorters: SortField[]
  setSorters: (sorters: SortField[]) => void
  headCells: HeadCell<T>[]
  childComponent?: JSX.Element
}

export const TableHeader = <T extends Object>({
  orderInitial,
  orderByInitial,
  sorters,
  setSorters,
  headCells,
  childComponent
}: TableHeaderProps<T>) => {
  const [order, setOrder] = React.useState<Order>(orderInitial)
  const [orderBy, setOrderBy] = React.useState<keyof T>(orderByInitial)

  const createSortHandler =
    (property: keyof T) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }

  const onRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof T
  ) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)

    let newSorters = [...sorters]
    let sortField = sorters?.find(
      (sortedField) => sortedField.dataField === property
    )

    if (sortField) {
      sortField.sortOrder = sortField.sortOrder === 'DESC' ? 'ASC' : 'DESC'
    } else {
      sortField = {
        dataField: property.toString(),
        sortOrder: isAsc ? 'DESC' : 'ASC'
      }
      newSorters = [sortField]
    }

    setSorters(newSorters)
  }

  return (
    <TableHead>
      <TableRow>
        {childComponent}
        {headCells
          .filter((headCell: HeadCell<T>) => headCell.visible)
          .map((headCell) => (
            <TableCell
              key={headCell.id.toString()}
              align={headCell.numeric ? 'right' : 'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={
                orderBy === headCell.id && headCell.sortable ? order : false
              }
            >
              <TableSortLabel
                active={orderBy === headCell.id && headCell.sortable}
                direction={
                  orderBy === headCell.id && headCell.sortable ? order : 'asc'
                }
                onClick={
                  headCell.sortable ? createSortHandler(headCell.id) : undefined
                }
                hideSortIcon={!headCell.sortable}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box
                    component='span'
                    sx={{
                      border: 0,
                      clip: 'rect(0 0 0 0)',
                      height: 1,
                      margin: -1,
                      overflow: 'hidden',
                      padding: 0,
                      position: 'absolute',
                      top: 20,
                      width: 1
                    }}
                  >
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
                <Box
                  sx={{
                    color: '#E0E0E0',
                    display: 'flex',
                    zIndex: 100,
                    position: 'absolute',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    left: '-22px',
                    minHeight: '56px',
                    opacity: 1
                  }}
                >
                  <svg
                    className='MuiSvgIcon-root MuiDataGrid-iconSeparator'
                    focusable='false'
                    viewBox='0 0 24 24'
                    aria-hidden='true'
                  >
                    <path d='M11 19V5h2v14z'></path>
                  </svg>
                </Box>
              </TableSortLabel>
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  )
}
